import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { JusoAddress } from '../../../api/address';
import { AddressSearchDialog } from './AddressSearchDialog';

/**
 * 화면이 「주소 찾기」 단추 하나를 켜는 데 드는 것 — 열림 상태 · 창 · 고른 뒤 포커스.
 *
 *   const addr = useAddressSearchDialog({ onApply: (a) => { set('postalCode', a.zipNo); set('address', a.roadAddr); },
 *                                         focusAfterPick: () => detailRef.current });
 *   <button onClick={addr.open}>주소 찾기</button> … {addr.dialog}
 *
 * ★ 고르면 창을 닫고 **상세주소로 포커스**를 옮긴다. 닫힐 때 공용 Modal 이 먼저 「주소 찾기」로 포커스를
 *   돌려주므로(useFocusTrap 정리), 옮기기는 **닫힌 뒤의 effect** 에서 한다 — 같은 틱에 하면 덮인다.
 * ★ 상세주소는 건드리지 않는다(고른 주소와 층·호수는 따로다).
 */
export function useAddressSearchDialog({
  onApply,
  focusAfterPick,
}: {
  onApply: (address: JusoAddress) => void;
  focusAfterPick: () => HTMLElement | null;
}): { open: () => void; dialog: ReactNode } {
  const [isOpen, setOpen] = useState(false);
  const picked = useRef(false);

  useEffect(() => {
    if (isOpen || !picked.current) return;
    picked.current = false;
    focusAfterPick()?.focus();
  }, [isOpen, focusAfterPick]);

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const pick = useCallback((a: JusoAddress) => {
    onApply(a);
    picked.current = true;
    setOpen(false);
  }, [onApply]);

  return { open, dialog: isOpen ? <AddressSearchDialog onPick={pick} onClose={close} /> : null };
}
